import { ChangeDetectionStrategy, Component, inject } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatFormField, MatLabel } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatInput } from '@angular/material/input'
import { MatMenuItem } from '@angular/material/menu'
import {
  ElderBasicPaneLayoutComponent,
  ElderDialogService,
  ElderPaneComponent,
  ElderSelectComponent,
  ElderToastService,
  ElderValidationErrorDirective,
} from '@elderbyte/ngx-starter'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { Observable, Observer, of, Subscriber, throwError } from 'rxjs'
import { filter, mergeMap } from 'rxjs/operators'
import { isProdMode } from '../../../../environments/environment'
import { truthyPredicate } from '../../../../functional.util'
import { ProcessPlanSelectDirective } from '../../process/process-plans/process-plan-select.directive'
import {
  ProdOrderTransitMigrationCommandType,
  ProdOrderTransitMigrationService,
} from '../prod-order-transit-migration.service'
import {
  WorkItemMigrationCommandType,
  WorkItemMigrationService,
} from '../work-item-migration.service'
import { FacilitySelectDirective } from '../../facilities/facility-select.directive'
import { PackageMigrationCommandType, PackageMigrationService } from '../package-migration.service'

@Component({
  selector: 'app-command-dispatcher',
  templateUrl: './command-dispatcher.component.html',
  styleUrls: ['./command-dispatcher.component.scss'],
  imports: [
    MatMenuItem,
    MatIcon,
    MatFormField,
    ElderSelectComponent,
    ProcessPlanSelectDirective,
    FormsModule,
    MatLabel,
    MatInput,
    ElderTranslatePipe,
    ElderBasicPaneLayoutComponent,
    ElderPaneComponent,
    ElderValidationErrorDirective,
    FacilitySelectDirective,
  ],
})
export class CommandDispatcherComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private workItemMigrationService = inject(WorkItemMigrationService)
  private prodOrderTransitMigrationService = inject(ProdOrderTransitMigrationService)
  private packageMigrationService = inject(PackageMigrationService)
  private toastService = inject(ElderToastService)
  private dialogService = inject(ElderDialogService)
  prodOrderTransitIds: string
  workItemIds: string
  sourceProcessPlanId: string
  targetProcessPlanId: string
  facility: string

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  protected emitProdOrderTransits(prodOrderTransitIds: string | undefined): void {
    const prodOrderTransitItemIds = this.parseArray(prodOrderTransitIds)
    const command = {
      commandType: ProdOrderTransitMigrationCommandType.RE_EMIT,
      prodOrderTransitItemIds,
    }
    const emitter = () => this.prodOrderTransitMigrationService.migrate(command)
    this.reEmitByIds(prodOrderTransitItemIds, emitter)
  }

  protected migrateWorkItemProcessPlanIds(): void {
    if (this.sourceProcessPlanId && this.targetProcessPlanId) {
      this.workItemMigrationService
        .migrate({
          type: WorkItemMigrationCommandType.PROCESS_PLANS,
          sourceProcessPlanId: this.sourceProcessPlanId,
          targetProcessPlanId: this.targetProcessPlanId,
        })
        .subscribe(this.getToastSubscriber())
    }
  }

  protected reEmitWorkItemIds(input: string | undefined): void {
    const workItemIds = this.parseArray(input)
    const emitter: () => Observable<unknown> = () =>
      this.workItemMigrationService.migrate({
        type: WorkItemMigrationCommandType.RE_EMIT,
        workItemIds,
      })
    this.reEmitByIds(workItemIds, emitter)
  }

  protected reEmitPackageIds(input: string | undefined): void {
    const packageIds = this.parseArray(input)
    const emitter: () => Observable<unknown> = () =>
      this.packageMigrationService.migrate({
        type: PackageMigrationCommandType.RE_EMIT,
        packageIds,
      })
    this.reEmitByIds(packageIds, emitter)
  }

  protected retryPreparation(): void {
    this.workItemMigrationService
      .migrate({
        type: WorkItemMigrationCommandType.PREPARE_INVALID,
        facility: this.facility,
      })
      .subscribe(this.getToastSubscriber())
  }

  protected retryEnrichments(): void {
    this.workItemMigrationService
      .migrate({
        type: WorkItemMigrationCommandType.ENRICH_INVALID,
        facility: this.facility,
      })
      .subscribe(this.getToastSubscriber())
  }

  /***************************************************************************
   *                                                                         *
   * Private methods                                                         *
   *                                                                         *
   **************************************************************************/

  private reEmitByIds(ids: string[], emitter: () => Observable<unknown>) {
    this.buildConfirmationGuard(ids).pipe(mergeMap(emitter)).subscribe(this.getToastSubscriber())
  }

  private parseArray(input: string): string[] {
    if (!input) {
      return []
    } else {
      return JSON.parse(input)
    }
  }

  private buildConfirmationGuard(ids: string[]): Observable<boolean> {
    if (ids.length === 0) {
      if (isProdMode()) {
        return throwError(() => new Error('Voller Emit nur für Entwicklung'))
      } else {
        return this.dialogService
          .showConfirm({
            title: 'Re-Emit',
            message: 'Re-emit all entities?',
          })
          .pipe(filter(truthyPredicate))
      }
    } else {
      return of(true)
    }
  }

  private getToastSubscriber(): Partial<Observer<unknown>> {
    return {
      next: (_: unknown) => this.toastService.info('messages.action_successful'),
      error: (err: unknown) => this.toastService.error('messages.action_failed', err),
    }
  }
}
